import { useState, useEffect } from 'react';
import { arrayOf, bool, func, string } from 'prop-types';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { FormattedMessage } from '../../util/reactIntl';
import { ensureCurrentUser } from '../../util/data';
import { propTypes } from '../../util/types';
import { pathByRouteName } from '../../util/routes';

import { Modal } from '../../components';

import EmailReminder from './EmailReminder';
import css from './ModalMissingInformation.module.css';

const MISSING_INFORMATION_MODAL_WHITELIST = [
  'LoginPage',
  'SignupPage',
  'AccountSettingsPage',
  'EmailVerificationPage',
  'PasswordResetPage',
];

const EMAIL_VERIFICATION = 'EMAIL_VERIFICATION';

function ModalMissingInformation(props) {
  const {
      currentUser = null,
      currentUserHasListings,
      currentUserHasOrders,
      location,
      className = null,
      containerClassName = null,
      rootClassName = null,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      onManageDisableScrolling,
      onResendVerificationEmail,
  } = props;
  const [showMissingInformationReminder, setShowMissingInformationReminder] = useState(null);
  const [hasSeenMissingInformationReminder, setHasSeenMissingInformationReminder] = useState(false);

  useEffect(() => {
    const user = ensureCurrentUser(currentUser);
    handleMissingInformationReminder(user, currentUserHasListings, currentUserHasOrders, location);
  }, [currentUser, currentUserHasListings, currentUserHasOrders, location]);

  function handleMissingInformationReminder(
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    newLocation
  ) {
    const routes = props.routeConfiguration;
    const whitelistedPaths = MISSING_INFORMATION_MODAL_WHITELIST.map(page =>
      pathByRouteName(page, routes)
    );

    // Is the current page whitelisted?
    const isPageWhitelisted = whitelistedPaths.includes(newLocation.pathname);

    // Track if path changes inside Page level component
    const pathChanged = newLocation.pathname !== props.location.pathname;
    const notRemindedYet = !showMissingInformationReminder && !hasSeenMissingInformationReminder;

    // Is the reminder already shown on current page
    const showOnPathChange = notRemindedYet || pathChanged;

    if (!isPageWhitelisted && showOnPathChange) {
      // Emails are sent when order is initiated
      // Customer is likely to get email soon when she books something
      // Provider email should work - she should get an email when someone books a listing
      const hasOrders = currentUserHasOrders === true;
      const hasListingsOrOrders = currentUserHasListings || hasOrders;

      const emailUnverified = !!currentUser.id && !currentUser.attributes.emailVerified;
      const emailVerificationNeeded = hasListingsOrOrders && emailUnverified;

      // Show reminder
      if (emailVerificationNeeded) {
        setShowMissingInformationReminder(EMAIL_VERIFICATION);
      }
    }
  }


  const user = ensureCurrentUser(currentUser);
  const classes = classNames(rootClassName || css.root, className);

  let content = null;

  const currentUserLoaded = user && user.id;
  if (currentUserLoaded) {
    if (showMissingInformationReminder === EMAIL_VERIFICATION) {
      content = (
        <EmailReminder
          className={classes}
          user={user}
          onResendVerificationEmail={onResendVerificationEmail}
          sendVerificationEmailInProgress={sendVerificationEmailInProgress}
          sendVerificationEmailError={sendVerificationEmailError}
        />
      );
    }
  }

  const closeButtonMessage = (
    <FormattedMessage id="ModalMissingInformation.closeVerifyEmailReminder" />
  );

  return (
    <Modal
      id="MissingInformationReminder"
      containerClassName={containerClassName}
      isOpen={!!showMissingInformationReminder}
      onClose={() => {
        setShowMissingInformationReminder(null);
        setHasSeenMissingInformationReminder(true);
      }}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling}
      closeButtonMessage={closeButtonMessage}
    >
      {content}
    </Modal>
  );
}

ModalMissingInformation.propTypes = {
  id: string.isRequired,
  className: string,
  rootClassName: string,
  containerClassName: string,

  currentUser: propTypes.currentUser,
  onManageDisableScrolling: func.isRequired,
  sendVerificationEmailError: propTypes.error,
  sendVerificationEmailInProgress: bool.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedModalMissingInformation = props => {
  const routeConfiguration = useRouteConfiguration();

  return <ModalMissingInformation routeConfiguration={routeConfiguration} {...props} />;
};

export default EnhancedModalMissingInformation;
